import { TableItemProps } from '@postscript/components/dist/esm/components/advanced/Table/types';

export interface ModalProps {
  minimumPerSubMMSCount: number;
  allMMSCountsSame: boolean;
  minimumPerSubSMSCount: number;
  allSMSCountsSame: boolean;
  numOfTotalMMSMsgs: number;
  numOfTotalMMSMembers: number;
  numOfTotalSMSMsgs: number;
  numOfTotalSMSMembers: number;
  projectedTotal: number;
  estimatedCarrierFees: number;
  tableRows: TableItemProps[];
  listOfMMSCountries: string[];
  listOfSMSCountries: string[];
}

export interface EstimatedCost {
  messageRate: number;
  messageCount: number;
  messageType: string;
  subscriberCount: number;
  countryCode: string;
  province?: string;
  estimatedAmountCents: number;
}

export interface MergedDataEstimatedCost {
  messageRateInCents: number;
  messageCounts: number;
  messageTypes: string[];
  subscriberCounts: number;
  countryCodes: string[];
  totalEstimatedAmountCents: number;
}

/**
 * Async multi-segment cost estimate.
 *
 * Each call estimates a single `messageType` across the include/exclude
 * segments. RCS campaigns run two jobs (an RCS variant + SMS) to produce a
 * cost range, since non-RCS-eligible recipients fall back to SMS.
 */
export const MessageType = {
  SMS: 'SMS',
  MMS: 'MMS',
  RCS_BASIC: 'RCS_BASIC',
  RCS_SINGLE: 'RCS_SINGLE',
} as const;

export type MessageType = typeof MessageType[keyof typeof MessageType];

export interface EstimatedCostMultiRequest {
  includeSegmentIds: number[];
  excludeSegmentIds: number[];
  messageType: MessageType;
  textBody: string;
}

export const EstimatedCostJobStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type EstimatedCostJobStatus =
  typeof EstimatedCostJobStatus[keyof typeof EstimatedCostJobStatus];

/**
 * A per-country cost row from the `/multi` endpoint.
 */
export interface MultiEstimatedCost {
  countryCode: string;
  subscriberCount: number;
  messageCount: number;
  messageType: MessageType;
  messageRate: number;
  estimatedAmountCents: number;
  estimatedCarrierFeeCents: number;
  estimatedMessageCostCents: number;
}

export interface EstimatedCostMultiJobData {
  estimatedCostsByCountryCode: MultiEstimatedCost[];
  totalEstimatedAmountCents: number;
  totalSubscriberCount: number;
}

// Both POST /multi and GET /multi/<job_id> return this job envelope; `data` is
// null until `status === 'completed'`.
export interface EstimatedCostMultiJob {
  jobId: string;
  status: EstimatedCostJobStatus;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  error: string | null;
  data: EstimatedCostMultiJobData | null;
}

/**
 * UI model for the "Billing Details" modal. Built by
 * `formatBillingDetails` from the RCS + SMS job results. For RCS campaigns the
 * total is a range (all-RCS low → all-SMS-fallback high); SMS-only campaigns
 * have a single total (low === high).
 */
export interface BillingDetailsChargeRow {
  label: string;
  rateLabel?: string;
  messageCount?: number;
  /** Prefix the message count with "Potential:" (RCS scenarios). */
  isPotential?: boolean;
  amountCents: number;
  isCarrierFee?: boolean;
}

export interface BillingDetailsRegion {
  countryCode: string;
  hasRcs: boolean;
  rcsMessageCount: number;
  rcsMemberCount: number;
  smsMemberCount: number;
  smsMessagesPerMember: number;
  rows: BillingDetailsChargeRow[];
  lowCents: number;
  highCents: number;
}

export interface BillingDetails {
  isRcs: boolean;
  isRange: boolean;
  isMultiCountry: boolean;
  regions: BillingDetailsRegion[];
  projectedTotalLowCents: number;
  projectedTotalHighCents: number;
}
